import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useApproveTimeOffRequest } from '../../hooks/useApproveTimeOffRequest';
import { useRejectTimeOffRequest } from '../../hooks/useRejectTimeOffRequest';

interface ApproveRejectActionsProps {
  requestId: string;
  requesterName: string;
  dayCount: number;
}

/** Visible only when the caller is the approver of the request's current pending step. */
export function ApproveRejectActions({
  requestId,
  requesterName,
  dayCount,
}: ApproveRejectActionsProps) {
  const { t } = useTranslation('timeOff');
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const [comment, setComment] = useState('');
  // `handleApprove`/`handleReject` keep running across the `await confirm(...)`
  // suspension while the user types into the dialog's textarea — reading
  // `comment` from the closure would use its stale value from click-time, not
  // what was typed. A ref always reflects the latest value at mutate-time.
  const commentRef = useRef('');

  function updateComment(value: string) {
    commentRef.current = value;
    setComment(value);
  }

  const approve = useApproveTimeOffRequest();
  const reject = useRejectTimeOffRequest();

  async function handleApprove() {
    const result = await confirm({
      title: t('approvals.confirmApprove.title'),
      description: t('approvals.confirmApprove.description', {
        name: requesterName,
        count: dayCount,
      }),
      confirmLabel: t('approvals.confirmApprove.confirmLabel'),
    });
    if (!result.confirmed) return;
    approve.mutate(
      { requestId, payload: { comment: commentRef.current || undefined } },
      { onSuccess: () => updateComment('') },
    );
  }

  async function handleReject() {
    const result = await confirm({
      title: t('approvals.confirmReject.title'),
      description: t('approvals.confirmReject.description', {
        name: requesterName,
        count: dayCount,
      }),
      confirmLabel: t('approvals.confirmReject.confirmLabel'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    reject.mutate(
      { requestId, payload: { comment: commentRef.current || undefined } },
      { onSuccess: () => updateComment('') },
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleReject}
          data-testid={`time-off-reject-btn-${requestId}`}
        >
          <X className="size-4" />
          {t('approvals.reject')}
        </Button>
        <Button size="sm" onClick={handleApprove} data-testid={`time-off-approve-btn-${requestId}`}>
          <Check className="size-4" />
          {t('approvals.approve')}
        </Button>
      </div>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
          data-testid="time-off-approve-reject-dialog"
        >
          <Textarea
            value={comment}
            onChange={(e) => updateComment(e.target.value)}
            placeholder={t('approvals.comment')}
            data-testid="time-off-approve-reject-comment"
          />
        </ConfirmDialog>
      )}
    </>
  );
}
